// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React from 'react';
import { Box, Button, FormField, SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import { RowEditor } from '../../hooks/useRowEditor';
import { Locale } from '../../utils/locales';
import { RowErrorSummary, SaveAlerts } from './ErrorMessages';
import LocaleSelect from './LocaleSelect';
import { ListColumn } from './ListTable';

/**
 * The columns, actions and alerts of a list edited with useRowEditor. Used by Common/ArcRows, the
 * base rates table and the TER group page.
 */

export interface RowItem<R> {
  row: R;
  index: number;
}

/**
 * Edit-mode row number, with the row's own error such as a duplicate. The number's element id is
 * `rowIdPrefix` and the row index, for scrolling to the row.
 */
export function rowNumberColumn<R, F extends string>(
  editor: RowEditor<R, F>,
  errorIdPrefix: string,
  rowIdPrefix: string
): ListColumn<RowItem<R>> {
  return {
    id: 'row',
    header: 'Row',
    editOnly: true,
    cell: ({ index }): JSX.Element => (
      <SpaceBetween size="xxs">
        <span id={`${rowIdPrefix}-${index}`}>{index + 1}</span>
        {editor.errorFor(index, 'row') && (
          <Box id={`${errorIdPrefix}-${index}`} color="text-status-error">
            {editor.errorFor(index, 'row')}
          </Box>
        )}
      </SpaceBetween>
    ),
  };
}

type LocaleField = 'sourceLocale' | 'targetLocale';

/**
 * A locale shown with `display`. While editing, a row added in this edit picks it with a
 * LocaleSelect (null until chosen); a loaded row keeps it read-only, since its locales are the
 * arc it prices and changing them means removing the row and adding another. A loaded locale that
 * cannot be saved as is, a stored Any or one missing from `locales`, can still be picked.
 */
export function localeColumn<
  R extends Record<LocaleField, string | null> & {
    original: { languagePair: Record<LocaleField, string | null> } | null;
  },
  F extends string
>(
  editor: RowEditor<R, F>,
  field: F & LocaleField,
  header: string,
  locales: Locale[],
  display: (code: string | null) => string
): ListColumn<RowItem<R>> {
  const listed = new Set(locales.map(locale => locale.code));
  // Judged on the loaded locale, so the picker stays while a wrong pick is corrected.
  const editable = ({ original }: R): boolean => {
    if (original === null) {
      return true;
    }
    const loaded: string | null = original.languagePair[field as LocaleField];
    return loaded === null || !listed.has(loaded);
  };
  return {
    id: field,
    header,
    cell: ({ row }): JSX.Element => <span>{display(row[field])}</span>,
    editCell: ({ row, index }): JSX.Element => (
      <FormField id={`${field}-${index}-field`} errorText={editor.errorFor(index, field)}>
        {editable(row) ? (
          <LocaleSelect
            id={`${field}-${index}`}
            value={row[field]}
            locales={locales}
            invalid={Boolean(editor.errorFor(index, field) || editor.errorFor(index, 'row'))}
            disabled={editor.submitting}
            onChange={(code): void => editor.changeRow(index, field, code)}
          />
        ) : (
          <span id={`${field}-${index}`}>{display(row[field])}</span>
        )}
      </FormField>
    ),
  };
}

/** Edit-mode button removing the row. */
export function removeRowColumn<R, F extends string>(
  editor: RowEditor<R, F>,
  buttonIdPrefix: string
): ListColumn<RowItem<R>> {
  return {
    id: 'actions',
    header: 'Actions',
    editOnly: true,
    cell: ({ index }): JSX.Element => (
      <Button
        id={`${buttonIdPrefix}-${index}`}
        disabled={editor.submitting}
        onClick={(): void => editor.removeRow(index)}
      >
        Remove
      </Button>
    ),
  };
}

/**
 * Edit, then Add row, Cancel and Save while editing. Edit stays disabled until the page is `ready`
 * to edit, and Save until something `changed`. Cancel discards the row edits, then calls
 * `onCancel` for any other state the page edits alongside them.
 */
export function EditActions<R, F extends string>({
  editor,
  ids,
  ready,
  changed,
  onCancel,
}: {
  editor: RowEditor<R, F>;
  ids: { edit: string; add: string; cancel: string; save: string };
  ready: boolean;
  changed: boolean;
  onCancel?: () => void;
}): JSX.Element {
  const cancel = (): void => {
    editor.reset();
    if (onCancel) {
      onCancel();
    }
  };

  if (!editor.editing) {
    return (
      <Button id={ids.edit} disabled={!ready} onClick={editor.startEditing}>
        Edit
      </Button>
    );
  }

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button id={ids.add} iconName="add-plus" disabled={editor.submitting} onClick={editor.addRow}>
        Add row
      </Button>
      <Button id={ids.cancel} variant="link" disabled={editor.submitting} onClick={cancel}>
        Cancel
      </Button>
      <Button
        id={ids.save}
        variant="primary"
        loading={editor.submitting}
        disabled={editor.submitting || !changed}
        onClick={editor.save}
      >
        Save
      </Button>
    </SpaceBetween>
  );
}

/** The outcome of a failed save and the rows left to fix. Element ids start with `idPrefix`. */
export function RowEditorAlerts<R, F extends string>({
  editor,
  idPrefix,
  conflictHeader,
  onReload,
}: {
  editor: RowEditor<R, F>;
  idPrefix: string;
  conflictHeader: string;
  onReload: () => void;
}): JSX.Element {
  return (
    <>
      <SaveAlerts
        idPrefix={idPrefix}
        conflictHeader={conflictHeader}
        conflict={editor.conflict}
        formErrors={editor.formErrors}
        onReload={onReload}
      />
      <RowErrorSummary
        idPrefix={idPrefix}
        rowNumbers={editor.errorRowNumbers}
        onShowFirst={editor.showFirstError}
      />
    </>
  );
}
